import { line as lineD3, curveLinear, max, min } from "d3";
import { dateInterpolate } from "@/_core/charts/D3ChartHoverHelpers";
export class Candle {
  constructor(svg, svgAux = null) {
    this.svg = svg;
    this.svgAux = svgAux ? svgAux : svg;
    this.upColor = "";
    this.dnColor = "";
  }

  candles({
    data,
    upColor,
    dnColor,
    customName,
    opacityUp = 1,
    opacityDn = 1,
    upColorBackground = null,
    dnColorBackground = null,
    lineWidth = 1,
    customX = false,
    deltaBand = 1 / 22,
    xRange = [],
    addId = false,
    idField = "id",
    xIsDate = false,
    fieldStick1 = "stick",
    fieldStick2 = "stick2",
    bandWidthAux = 0,
    offsetBandWidth = 0,
    oldChart = true,
    barWidth = 0.7
  }) {
    this.upColor = upColor;
    this.dnColor = dnColor;
    const svg = this.svg;
    if (!upColorBackground) upColorBackground = upColor;

    if (!dnColorBackground) {
      dnColorBackground = dnColor;
    }
    // ======= Initial objects =======
    let bandWidth = xIsDate ? bandWidthAux : svg.x.bandwidth();

    const candleWidth = bandWidth * barWidth + 1;
    const delta = (bandWidth - candleWidth) / 2;
    const deltaCustom = candleWidth * deltaBand;

    if (oldChart) {
      // Add Sticks
      this.svg[fieldStick1] = this.setData("sticks", customName, data);
      this.setRect(
        fieldStick1,
        customX,
        xRange,
        deltaCustom,
        lineWidth,
        offsetBandWidth,
        bandWidth,
        "stick",
        customName,
        addId,
        idField,
        false
      );

      // Add candle shadows
      this.svg[fieldStick2] = this.setData("sticks2", customName, data);
      this.setRect(
        fieldStick2,
        customX,
        xRange,
        deltaCustom,
        lineWidth,
        offsetBandWidth,
        bandWidth,
        "stick2",
        customName,
        addId,
        idField,
        true
      );
    } else {
      let series1 = data.map(function(d) {
        return {
          name: d.close >= d.open ? "up" : "dn",
          values: [{ date: d.date, y: d.high }].concat([
            { date: d.date, y: Math.max(d.open, d.close) }
          ])
        };
      });
      let series2 = data.map(function(d) {
        return {
          name: d.close >= d.open ? "up" : "dn",
          values: [{ date: d.date, y: d.low }].concat([
            { date: d.date, y: Math.min(d.open, d.close) }
          ])
        };
      });
      let newData = {
        name: "sticks",
        series: series1.concat(series2)
      };

      let line = lineD3()
        .curve(curveLinear) // .curve(curveMonotoneX)
        .defined(d => d.y)
        .x(d => this.svg.x(d.date) + bandWidth / 2 - offsetBandWidth)
        .y(d => this.svg.y(d.y));

      this.svg[fieldStick1] = this.svgAux
        .append("g")
        .attr("class", "sticks" + customName)
        .attr("fill", "none")
        .attr("stroke-linejoin", "round")
        .attr("stroke-linecap", "round")
        .selectAll("path")
        .data(newData.series, d => d.id)
        .join("path")
        .style("stroke", d => (d.name === "up" ? upColor : dnColor))
        .style("stroke-width", lineWidth)
        .attr("d", d => line(d.values))
        .attr("class", function(d, i) {
          const id = addId
            ? idField === "date"
              ? +d.values[0].date
              : +d[idField]
            : i;
          return `stick${customName}${id}`;
        })
        .style("shape-rendering", "crispEdges")
        .style("vector-effect", "non-scaling-stroke");
    }

    // add candle bodies
    this.svg["candles"] = this.setData("candles", customName, data);

    this.svg["candles"]
      .selectAll("rect") // candle bodies are called candles
      .data(function(d) {
        return d;
      })
      .enter()
      .append("rect")
      .attr("x", d => {
        return customX
          ? dateInterpolate(
              xRange,
              d.date,
              this.svg.x,
              this.svg.x.bandwidth()
            ) +
              deltaCustom / 2
          : this.svg.x(d.date) + delta;
      })
      .attr("y", d => {
        return this.svg.y(max([d.open, d.close]));
      })
      .attr("class", (d, i) => {
        return "candle" + customName + "" + (addId ? +d[idField] : i);
      })
      .attr("height", d => {
        return Math.max(
          0.000001,
          this.svg.y(min([d.open, d.close])) -
            this.svg.y(max([d.open, d.close]))
        );
      })
      .attr("width", customX ? deltaCustom : candleWidth)
      .attr("stroke-width", lineWidth)
      .attr("stroke", d => {
        return d.close > d.open ? upColor : dnColor;
      })
      .style("fill", d => {
        return d.close > d.open ? upColorBackground : dnColorBackground;
      })
      .attr("fill-opacity", d => {
        return d.close > d.open ? opacityUp : opacityDn;
      });

    return this.svg;
  }

  // GENERAL METHODS

  setData(className, customName, data) {
    let figure = this.svgAux
      .selectAll("." + className + customName)
      .data([data])
      .enter()
      .append("g")
      .attr("class", className + customName);

    return figure;
  }

  setRect(
    fieldStick,
    customX,
    xRange,
    deltaCustom,
    lineWidth,
    offsetBandWidth,
    bandWidth,
    className,
    customName,
    addId,
    idField,
    isShadow
  ) {
    const svg = this.svg;

    svg[fieldStick]
      .selectAll("rect")
      .data(d => {
        return d;
      })
      .enter()
      .append("rect")
      .attr("x", function(d) {
        return customX
          ? dateInterpolate(xRange, d.date, svg.x, svg.x.bandwidth()) +
              deltaCustom -
              lineWidth / 2 -
              offsetBandWidth
          : svg.x(d.date) + bandWidth / 2 - offsetBandWidth;
      })
      .attr("stroke-width", lineWidth)
      .attr("y", d =>
        isShadow ? svg.y(Math.min(d.open, d.close)) : svg.y(d.high)
      )
      .attr(
        "class",
        (d, i) => className + customName + "" + (addId ? +d[idField] : i)
      )
      .attr("height", d => {
        let height = isShadow
          ? svg.y(d.low) - svg.y(Math.min(d.open, d.close))
          : svg.y(Math.max(d.open, d.close)) - svg.y(d.high);
        return Math.max(0.0000001, height);
      })
      .attr("width", 1)
      .style("fill", d => (d.close > d.open ? this.upColor : this.dnColor))
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");
  }
}
